import { VectorTileLayer } from '@ethings/ui-toolkit/map';
import { Theme, useTheme } from '@mui/material';
import { Box, SxProps } from '@mui/system';
import { Control } from 'leaflet';
import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-leaflet';
import { HERE_MAP_API_KEY } from '../../constants';
import { UI_TEMPLATE_DRIVEN_MAP_LAYERS_FLAG } from '../../feature-flags';
import { useConfig, useMapContext, useUiTemplate } from '../../hooks';
import { BaseLayers, MapStorageKeys, ToggleLayers } from '../../types';
import { MAP_LAYERS_TEMPLATE } from '../../ui-templates';
import { useLocalStorage } from '../../util/local-storage';
import { useSubscribeToMapDimensions } from '../../util/maps';
import { ErrorBoundary } from '../error-boundary';
import { useBoolFlag, useFlags } from '../flags';
import { LayerMenu, LayerMenuTemplate } from '../layer-menu';
import {
  getBaseLayerMapStyle,
  getLayerComponentForType,
  mapTemplateTypeToBaseLayer,
  mapTemplateTypeToToggleLayer
} from '../layer-menu/layer-mapping-new';
import { useMapLayers } from './hooks';
import { LayerSelection } from './layer-selection/layer-selection';
import { baseLayers, toggleLayers } from './layer-selection/map-layers-default';

const JAPAN_LOCALE = 'ja';
const KOREA_LOCALE = 'ko';
export const DEFAULT_LAYER = (navigator.language === JAPAN_LOCALE || navigator.language === KOREA_LOCALE)
  ? BaseLayers.parcelBoundaries
  : BaseLayers.classic;

export interface LayerTypes {
  layer: BaseLayers;
}

interface Props {
  disabledLayers?: ToggleLayers[];
  drawerLayerSelection?: boolean;
  layerSelectionButtonSx?: SxProps<Theme>;
  shouldShowLayerSelection?: boolean;
}

export const MapLayerController: FC<Props> = ({ disabledLayers = [], drawerLayerSelection = false, layerSelectionButtonSx, shouldShowLayerSelection = true }) => {
  const config = useConfig();
  const flags = useFlags();
  const isUiTemplateDrivenMapLayers = useBoolFlag(UI_TEMPLATE_DRIVEN_MAP_LAYERS_FLAG);
  const { template, loaded } = useUiTemplate('map-layers', MAP_LAYERS_TEMPLATE);
  const flaggedLayers = toggleLayers(flags);
  const flaggedBaseLayers = useMemo(() => baseLayers(flags), [flags]);
  const map = useMap();
  const theme = useTheme();
  const { selectedLayers, setSelectedLayers, shouldUseLocalStorage } = useMapLayers();
  const [storedBaseLayerLS, setBaseLayerLS] = useLocalStorage<LayerTypes>(MapStorageKeys.MAP_BASE_LAYER, { layer: DEFAULT_LAYER });
  const [storedBaseLayerState, setBaseLayerState] = useState<LayerTypes>({ layer: DEFAULT_LAYER });
  const storedBaseLayer = shouldUseLocalStorage ? storedBaseLayerLS : storedBaseLayerState;
  const setBaseLayer = shouldUseLocalStorage ? setBaseLayerLS : setBaseLayerState;
  const baseLayer = useMemo(() => {
    if (flaggedBaseLayers.length) {
      if (flaggedBaseLayers.find((item) => item.label === storedBaseLayer.layer)) return storedBaseLayer;
      return { layer: flaggedBaseLayers[0].label };
    }
    return { layer: BaseLayers.classic };
  }, [flaggedBaseLayers, storedBaseLayer]);

  const [open, setOpen] = useState(false);
  const { setMapDimming } = useMapContext();

  const { zoomLevel, width, height } = useSubscribeToMapDimensions();

  useEffect(() => {
    if (map) {
      map.getContainer().style.cursor = 'grab';

      map.on('movestart', () => {
        map.getContainer().style.cursor = 'grabbing';
      });

      map.on('moveend', () => {
        map.getContainer().style.cursor = 'grab';
      });

      return () => {
        map.off('movestart');
        map.off('moveend');
      };
    }
  }, [map]);

  const activeLayers = flaggedLayers.filter((layer) => {
    if (layer.minZoomLevel && zoomLevel < layer.minZoomLevel) {
      return false;
    }
    if (layer.maxBoundingBoxSize && (width > layer.maxBoundingBoxSize || height > layer.maxBoundingBoxSize)) {
      return false;
    }
    if (disabledLayers.includes(layer.label)) return false;

    return true;
  });

  const handleToggleChange = (layer: ToggleLayers): void => {
    if (selectedLayers.includes(layer)) {
      setMapDimming(layer, false);
      setSelectedLayers(selectedLayers.filter((item) => item !== layer));
      return;
    }
    setSelectedLayers([...selectedLayers, layer]);
  };

  const handleBaseChange = (label: BaseLayers): void => {
    setBaseLayer({ layer: label });
    setOpen(false);
    map.attributionControl.remove();
    map.addControl(new Control.Attribution());
  };

  const layerComponent = useMemo(() => {
    const possibleLayers = flaggedBaseLayers.length ? flaggedBaseLayers : baseLayers();
    const selectedLayer = possibleLayers.find((mapLayer) => mapLayer.label === baseLayer.layer);

    if (!selectedLayer) return;

    if (selectedLayer.label === BaseLayers.classic) {
      return <>{selectedLayer.layerComponent}</>;
    }

    return <>
      <VectorTileLayer scene={selectedLayer.mapStyle(theme, config.hereMaps?.apiKey ?? HERE_MAP_API_KEY)} apiKey={config.hereMaps?.apiKey ?? HERE_MAP_API_KEY} />
      {selectedLayer.layerComponent}
    </>;
  }, [flaggedBaseLayers, baseLayer.layer, config.hereMaps?.apiKey, theme]);

  const templateLayerComponent = useMemo(() => {
    if (!isUiTemplateDrivenMapLayers || !loaded || !template) return null;

    const templateData = template as LayerMenuTemplate;
    const baseLayerType = baseLayer.layer;

    const baseLayerDef = templateData.baseLayers?.find((l) => mapTemplateTypeToBaseLayer(l.type) === baseLayerType);

    if (!baseLayerDef) return null;

    const layerComp = getLayerComponentForType(baseLayerDef.type, flags);
    const mapStyle = getBaseLayerMapStyle(baseLayerDef.type, flags);

    if (!layerComp) return null;

    if (baseLayerType === BaseLayers.classic) {
      return <>{layerComp}</>;
    }

    return <>
      {mapStyle && <VectorTileLayer scene={mapStyle(theme, config.hereMaps?.apiKey ?? HERE_MAP_API_KEY)} apiKey={config.hereMaps?.apiKey ?? HERE_MAP_API_KEY} />}
      {layerComp}
    </>;
  }, [isUiTemplateDrivenMapLayers, loaded, template, baseLayer.layer, flags, theme, config.hereMaps?.apiKey]);

  const templateToggleLayers = useMemo(() => {
    if (!isUiTemplateDrivenMapLayers || !loaded || !template) return null;

    const templateData = template as LayerMenuTemplate;
    const allToggleLayers = [...(templateData.detailLayers || []), ...(templateData.entityLayers || [])];

    return selectedLayers.map((selectedLayer, i: number) => {
      if (!selectedLayer) return null;
      if (disabledLayers.includes(selectedLayer)) return null;

      const layerDef = allToggleLayers.find((l) => mapTemplateTypeToToggleLayer(l.type) === selectedLayer);

      if (!layerDef) return null;

      const layerComp = getLayerComponentForType(layerDef.type, flags);

      if (!layerComp) return null;

      return <Box key={i}>{layerComp}</Box>;
    });
  }, [isUiTemplateDrivenMapLayers, loaded, template, selectedLayers, disabledLayers, flags]);

  const legacyLayerSelection = (
    <LayerSelection
      handleBaseChange={handleBaseChange}
      handleToggleChange={handleToggleChange}
      mapBaseLayers={flaggedBaseLayers}
      activeLayers={activeLayers}
      mapToggleLayers={flaggedLayers}
      open={open}
      selectedBaseLayer={baseLayer.layer}
      selectedToggleLayers={selectedLayers}
      setOpen={setOpen}
      drawerLayerSelection={drawerLayerSelection}
      buttonSx={layerSelectionButtonSx ?? ((theme) => ({ position: 'absolute', right: theme.spacing(2), bottom: 116, zIndex: 500 }))}
    />
  );

  const getTemplateLayerSelection = (): ReactElement | null => {
    if (!loaded || !template) return null;

    const templateData = template as LayerMenuTemplate;

    return (
      <ErrorBoundary fallback={legacyLayerSelection}>
        <LayerMenu
          baseLayers={templateData.baseLayers || []}
          detailLayers={templateData.detailLayers || []}
          entityLayers={templateData.entityLayers || []}
          disabledLayers={disabledLayers}
          buttonSx={layerSelectionButtonSx}
          handleBaseChange={handleBaseChange}
          selectedBaseLayer={baseLayer.layer}
          shouldShowLayerSelection={shouldShowLayerSelection}
          zoomLevel={zoomLevel}
          mapWidth={width}
          mapHeight={height}
        />
      </ErrorBoundary>
    );
  };

  return <>
    {/* Used solely by automated tests to read the zoom level */}
    <span data-testid="map-zoom-level" id="map-zoom-level" data-zoomLevel={zoomLevel} />

    {isUiTemplateDrivenMapLayers ? templateLayerComponent : layerComponent}

    {isUiTemplateDrivenMapLayers ? templateToggleLayers : selectedLayers.map((checkItem, i: number) => {
      if (!checkItem) return;
      return (
        <Box key={i}>
          {activeLayers.find((layer) => layer.label === checkItem)?.layerComponent}
        </Box>
      );
    })}

    {shouldShowLayerSelection && (
      isUiTemplateDrivenMapLayers ? getTemplateLayerSelection() : legacyLayerSelection
    )}
  </>;
};
